/** @format */

export const normalizeLayoutItem = layoutItem => {
  if (!layoutItem) return layoutItem;

  const minH = layoutItem.minH ?? 1;
  const maxH = layoutItem.maxH ?? Infinity;
  let h = layoutItem.h ?? minH;

  if (minH > maxH) {
    return { ...layoutItem, minH: h, maxH: h, h };
  }

  if (h < minH) {
    return { ...layoutItem, minH: h, h };
  }

  if (h > maxH) {
    return { ...layoutItem, h: maxH };
  }

  return layoutItem;
};

export const normalizeLayouts = (layouts: Record<string, unknown[]> = {}) =>
  Object.fromEntries(
    Object.entries(layouts).map(([breakpoint, layout]) => [
      breakpoint,
      (Array.isArray(layout) ? layout : []).map(normalizeLayoutItem)
    ])
  );

const GRID_BREAKPOINT_WIDTHS = {
  xxl: 1919,
  xl: 1439,
  lg: 1023,
  md: 767,
  sm: 575,
  xs: 0
};

const GRID_COLS = { xxl: 12, xl: 12, lg: 12, md: 2, sm: 2, xs: 1 };

export const getBreakpointFromWidth = (width = typeof window !== 'undefined' ? window.innerWidth : 1920) => {
  const sorted = Object.keys(GRID_BREAKPOINT_WIDTHS).sort(
    (a, b) => GRID_BREAKPOINT_WIDTHS[a] - GRID_BREAKPOINT_WIDTHS[b]
  );
  let matching = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const breakpoint = sorted[i];
    if (width > GRID_BREAKPOINT_WIDTHS[breakpoint]) matching = breakpoint;
  }
  return matching;
};

export const getBreakpoint = () => getBreakpointFromWidth();

const commonSize = (breakpoint, item) => {
  if (breakpoint === 'md')
    return {
      w: (item.w > 6 ? 2 : 1) || 1,
      h: 6,
      minW: 1,
      maxW: 2,
      minH: 3,
      maxH: 9
    };
  if (breakpoint === 'sm') return { w: 1, h: 6, minW: 1, maxW: 1, minH: 3, maxH: 9 };
  if (breakpoint === 'xs') return { w: 1, h: 6, minW: 1, maxW: 1, minH: 3, maxH: 9 };
};

const calcSizeForBreakpoint = (size, item, breakpoint) => {
  switch (size) {
    case 'small':
      if (['xxl', 'xl', 'lg'].includes(breakpoint)) {
        return { w: 3, h: 3, minW: 1, maxW: 4, minH: 3, maxH: 3 };
      }
      return { w: 1, h: 3, minW: 1, maxW: 2, minH: 2, maxH: 3 };
    case 'normal':
      if (['xxl', 'xl', 'lg'].includes(breakpoint)) {
        return { w: 3, h: 6, minW: 1, maxW: 9, minH: 3, maxH: 9 };
      }
      return commonSize(breakpoint, item);
    case 'medium':
      if (['xxl', 'xl', 'lg'].includes(breakpoint)) {
        if (item.type === 'contact') {
          return { w: 4, h: 3, minW: 1, maxW: 8, minH: 2, maxH: 3 };
        }
        return { w: 4, h: 6, minW: 1, maxW: 8, minH: 2, maxH: 6 };
      }
      return commonSize(breakpoint, item);
    case 'large':
      if (['xxl', 'xl', 'lg'].includes(breakpoint)) {
        return { w: 6, h: 6, minW: 1, maxW: 8, minH: 2, maxH: 6 };
      }
      return commonSize(breakpoint, item);
    case 'wide':
      if (['xxl', 'xl', 'lg'].includes(breakpoint)) {
        return { w: 8, h: 6, minW: 1, maxW: 8, minH: 2, maxH: 6 };
      }
      return commonSize(breakpoint, item);
    case 'xwide':
      if (['xxl', 'xl', 'lg'].includes(breakpoint)) {
        return { w: 10, h: 6, minW: 1, maxW: 10, minH: 2, maxH: 6 };
      }
      return commonSize(breakpoint, item);
    case '2xl':
    case 'fullWidth':
      if (['xxl', 'xl', 'lg'].includes(breakpoint)) {
        if (item.type === 'information') {
          return { w: 12, h: 4, minW: 1, maxW: 12, minH: 1, maxH: 7 };
        }
        if (item.type === 'hero') {
          return { w: 12, h: 7, minW: 1, maxW: 12, minH: 1, maxH: 7 };
        }
        if (item.type === 'video') {
          return { w: 12, h: 6, minW: 1, maxW: 12, minH: 1, maxH: 6 };
        }
        return { w: 12, h: 7, minW: 1, maxW: 12, minH: 1, maxH: 7 };
      }
      if (['md', 'sm'].includes(breakpoint)) {
        return { w: 2, h: 6, minW: 1, maxW: 2, minH: 2, maxH: 6 };
      }
      return commonSize(breakpoint, item);
    default:
      if (['xxl', 'xl', 'lg'].includes(breakpoint)) {
        return { w: 4, h: 3, minW: 1, maxW: 5, minH: 2, maxH: 6 };
      }
      return commonSize(breakpoint, item);
  }
};

export const calcSize = (size, item) => calcSizeForBreakpoint(size, item, getBreakpoint());

export const calculateItemPositions = (widgets, totalWidthOfItems, cols, breakpoint) => {
  const gridWidth = cols ?? (typeof window !== 'undefined' && window.innerWidth < 1024 ? 2 : 12);
  const resolvedBreakpoint = breakpoint ?? (gridWidth === 1 ? 'xs' : gridWidth === 2 ? 'md' : 'lg');
  const positions = [];
  const grid = Array(100)
    .fill(null)
    .map(() => Array(gridWidth).fill(false));
  let maxY = 0;

  const isSpaceAvailable = (x, y, width, height) => {
    if (x + width > gridWidth) return false;
    for (let i = y; i < y + height; i++) {
      for (let j = x; j < x + width; j++) {
        if (grid[i] && grid[i][j]) return false;
      }
    }
    return true;
  };

  const findBestPosition = (width, height) => {
    let bestX = 0;
    let bestY = 0;
    let found = false;

    // First try to fill any gaps in existing rows
    for (let y = 0; y <= maxY; y++) {
      for (let x = 0; x < gridWidth; x++) {
        if (isSpaceAvailable(x, y, width, height)) {
          // Check if this position connects to existing items
          const hasAdjacentItem =
            (x > 0 && grid[y][x - 1]) || // Left
            (x + width < gridWidth && grid[y] && grid[y][x + width]) || // Right
            (y > 0 && grid[y - 1][x]); // Top

          if (hasAdjacentItem) {
            return { x, y };
          }

          if (!found) {
            bestX = x;
            bestY = y;
            found = true;
          }
        }
      }
    }

    // If no gaps found, place at the bottom
    if (!found) {
      bestY = maxY;
      // Try to place it at the leftmost position
      for (let x = 0; x < gridWidth; x++) {
        if (isSpaceAvailable(x, maxY, width, height)) {
          bestX = x;
          break;
        }
      }
    }

    return { x: bestX, y: bestY };
  };

  const markOccupiedSpace = (x, y, width, height) => {
    for (let i = y; i < y + height; i++) {
      for (let j = x; j < x + width; j++) {
        if (grid[i]) grid[i][j] = true;
      }
    }
    maxY = Math.max(maxY, y + height);
  };

  if (widgets.length) {
    widgets.forEach((item, i) => {
      const sizeForGrid = calcSizeForBreakpoint(item.size, item, resolvedBreakpoint);
      const itemWidth = item.w || sizeForGrid.w;
      const itemHeight = item.h || sizeForGrid.h;

      const position = findBestPosition(itemWidth, itemHeight);
      positions.push(position);
      markOccupiedSpace(position.x, position.y, itemWidth, itemHeight);
    });
  }

  return positions;
};

const buildLayoutItemForBreakpoint = (item, index, itemPositions, breakpoint, totalWidthOfItems = 0) =>
  normalizeLayoutItem({
    i: item.i || String(index),
    x: itemPositions[index] != null ? itemPositions[index].x : item.x != null ? item.x : totalWidthOfItems % 12,
    y:
      itemPositions[index] != null
        ? itemPositions[index].y
        : item.y != null
          ? item.y
          : Math.floor(totalWidthOfItems / 12),
    ...calcSizeForBreakpoint(item.size, item, breakpoint)
  });

export const generateLayout = (item, index, totalWidthOfItems, changeTotalWidthOfItems, itemPositions) => {
  const breakpoint = getBreakpoint();
  changeTotalWidthOfItems(calcSizeForBreakpoint(item.size, item, breakpoint).w);
  return buildLayoutItemForBreakpoint(item, index, itemPositions, breakpoint, totalWidthOfItems);
};

export const buildResponsiveLayouts = widgets => {
  const layouts = {};

  Object.keys(GRID_BREAKPOINT_WIDTHS).forEach(breakpoint => {
    const cols = GRID_COLS[breakpoint];
    const positions = calculateItemPositions(widgets, 0, cols, breakpoint);
    layouts[breakpoint] = widgets.map((item, index) =>
      buildLayoutItemForBreakpoint(item, index, positions, breakpoint)
    );
  });

  return normalizeLayouts(layouts);
};
